const lista = (valor) => (Array.isArray(valor) ? valor : []);
const id = (valor) => (valor == null ? '' : String(valor));
const referenciaId = (valor) => id(valor && typeof valor === 'object' ? valor.id : valor);

// Uma ocorrência gerada por regime não pode virar manual quando sua linha
// estrutural desaparece. Manuais reais continuam válidos sem regime/vínculo.
const cardDeRegimeOrfao = (linha, card) =>
	linha?.origem_linha === 'MANUAL' &&
	!linha.regime_vinculo_id && !linha.regime_escala?.id &&
	Boolean(card.origem?.regime_vinculo_id || card.origem?.alocacao_origem_chave);

const limparRegimesOrfaos = (ambiente) => ({
	...ambiente,
	linhas: ambiente.linhas.flatMap((linha) => {
		const cards = lista(linha.cards).filter((card) => !cardDeRegimeOrfao(linha, card));
		const celulas = lista(linha.celulas).map((celula) => {
			const validos = lista(celula.cards).filter((card) => !cardDeRegimeOrfao(linha, card));
			return validos.length === lista(celula.cards).length ? celula : {
				...celula, cards: validos, quantidade_cards: validos.length,
				...(validos.length ? {} : { status: 'NAO_ATRIBUIDO', editavel: false, sugerido: false }),
			};
		});
		const tinhaOrfaos = lista(linha.cards).some((card) => cardDeRegimeOrfao(linha, card)) ||
			lista(linha.celulas).some((celula) => lista(celula.cards).some((card) => cardDeRegimeOrfao(linha, card)));
		if (tinhaOrfaos && !cards.length && !celulas.some((celula) => celula.cards.length) && !lista(linha.ausencias).length) return [];
		return [{ ...linha, ...(Array.isArray(linha.cards) ? { cards } : {}), celulas }];
	}),
});

// O resumo com cards já associa cada ocorrência à sua linha. Apenas monta
// as células de apresentação; não calcula recorrências nem cria IDs.
export const adaptarResumoComCards = (resumo) => {
	const falha = (message) => ({ error: { message } });
	if (resumo?.modo !== 'resumo' || resumo?.capacidades?.cards_incluidos !== true) {
		return falha('O backend ainda não disponibiliza os cards no resumo.');
	}
	if (!Array.isArray(resumo.linhas) || !Array.isArray(resumo.calendario) ||
		resumo.linhas.some((linha) => !Array.isArray(linha.cards))) {
		return falha('Resposta de cards incompleta. Atualize a consulta.');
	}
	const datas = new Set(resumo.calendario.map((dia) => dia.data));
	const linhas = [];
	for (const linha of resumo.linhas) {
		const vigenciaFim = String(linha.regime_escala?.vigencia_fim || linha.regime_escala?.data_fim || linha.vigencia_fim || linha.regime_vinculo?.vigencia_fim || linha.regime_vinculo?.data_fim || '').slice(0, 10);
		const dentroDaVigencia = (data) => !/^\d{4}-\d{2}-\d{2}$/.test(vigenciaFim) || data < vigenciaFim;
		const porDia = new Map();
		for (const card of linha.cards) {
			const data = card.horario?.data;
			if (card.id == null || !datas.has(data) || card.associacao_ambiente?.linha_id !== linha.linha_id) {
				return falha('O resumo retornou um card sem identidade, data ou associação válida.');
			}
			if (!dentroDaVigencia(data)) continue;
			const cards = porDia.get(data) || [];
			cards.push({ ...card, setor: card.setor || { id: card.setor_id ?? linha.setor?.id },
				escala_mensal: card.escala_mensal || { id: card.escala_mensal_id } });
			porDia.set(data, cards);
		}
		const sinais = new Map();
		for (const ausencia of lista(linha.ausencias)) {
			for (const card of lista(ausencia.cards)) {
				const data = card.horario?.data;
				if (!datas.has(data) || porDia.has(data) || !dentroDaVigencia(data)) continue;
				const cards = sinais.get(data) || [];
				cards.push({ ...card, id: null, origem: { ...card.origem, ausencia_id: card.origem?.ausencia_id ?? ausencia.id },
					acoes: { pode_editar: false, pode_excluir: false, pode_trocar: false }, acoes_disponiveis: [] });
				sinais.set(data, cards);
			}
		}
		linhas.push({ ...linha, celulas: resumo.calendario.map((dia) => {
			const cards = porDia.get(dia.data) || sinais.get(dia.data) || [];
			return { ...dia, cards, quantidade_cards: cards.length, turno_explicito_resumo: true,
				status: cards.length ? (cards[0].tipo_item === 'ALOCACAO' ? 'ATRIBUIDO' : cards[0].tipo_item) : 'NAO_ATRIBUIDO',
				editavel: cards.some((card) => card.id != null && (card.acoes?.pode_editar || card.acoes?.pode_excluir || card.acoes?.pode_trocar)) };
		}) });
	}
	return limparRegimesOrfaos({ ...resumo, linhas });
};

const contextoDivergente = (resumo, grade) => {
	const contextoResumo = resumo?.contexto || {};
	const contextoGrade = grade?.contexto || {};
	return ['estabelecimento', 'mes_referencia', 'modalidade'].some((campo) => {
		const a = referenciaId(contextoResumo[campo]);
		const b = referenciaId(contextoGrade[campo]);
		return a !== '' && b !== '' && a !== b;
	});
};

const cardsDaGrade = (grade = {}) => {
	const visualizacao = grade?.grade || grade?.visualizacao || {};
	const setores = lista(visualizacao.setores);
	if (setores.length > 0) {
		return setores.flatMap((setor) =>
			lista(setor.linhas).flatMap((linha) =>
				lista(linha.celulas).flatMap((celula) =>
					lista(celula.cards).map((card) => ({ card, setor, data: celula.data })),
				),
			),
		);
	}
	return lista(grade.itens).map((card) => ({ card, setor: card.setor, data: card.horario?.data }));
};

const cardParaAmbiente = ({ card, setor, data }) => {
	const edicao = card.edicao || {};
	return {
		...card,
		horario: { ...(edicao.horario || {}), ...(card.horario || {}), data: card.horario?.data || edicao.horario?.data || data },
		setor: card.setor || setor || (edicao.contexto_competencia?.setor_id != null ? { id: edicao.contexto_competencia.setor_id } : null),
		...(card.vinculo_profissional === undefined && edicao.profissional?.vinculo_id != null
			? { vinculo_profissional: { id: edicao.profissional.vinculo_id } }
			: {}),
		...(card.escala_mensal === undefined && edicao.escala_mensal_id != null
			? { escala_mensal: { id: edicao.escala_mensal_id, regime: edicao.contexto_competencia?.regime } }
			: {}),
		observacao: card.observacao ?? card.resumo?.observacao ?? edicao.observacao ?? '',
	};
};

// Somente projeção de apresentação: a identidade operacional vem sempre do
// backend. Não tenta deduzir vínculo, regime ou recorrência a partir dos cards.
export const comporAmbienteResumo = ({ resumo = {}, grade = {} } = {}) => {
	const inconsistencias = [];
	const cardsOrfaos = [];
	const linhasPorId = new Map();
	const visualizacao = grade?.grade || grade?.visualizacao || {};
	const calendario = Array.isArray(resumo.calendario) ? resumo.calendario : lista(visualizacao.colunas);
	const diasCalendario = new Map(calendario.map((dia) => [id(dia.data), dia]));
	const adicionarLinha = (linha) => {
		const linhaId = id(linha?.linha_id);
		if (!linhaId) {
			inconsistencias.push({ codigo: 'LINHA_SEM_IDENTIDADE', linha });
			return null;
		}
		if (!linhasPorId.has(linhaId)) {
			linhasPorId.set(linhaId, { linha, cards: new Map(), historico: new Map() });
		}
		return linhasPorId.get(linhaId);
	};
	lista(resumo.linhas).forEach(adicionarLinha);

	const entradasCards = cardsDaGrade(grade);
	const contextoInvalido = contextoDivergente(resumo, grade);
	if (contextoInvalido) {
		inconsistencias.push({ codigo: 'CONTEXTO_DIVERGENTE' });
		cardsOrfaos.push(...entradasCards.map(({ card }) => card));
	} else {
		lista(grade.projecoes_historicas).forEach((projecao) => {
			const destino = adicionarLinha(projecao.linha);
			if (!destino) return;
			lista(projecao.dias).forEach((dia) => {
				if (diasCalendario.has(id(dia.data))) destino.historico.set(id(dia.data), dia);
			});
		});
		const idsProcessados = new Map();
		entradasCards.forEach((entrada) => {
			const { card, setor } = entrada;
			const linhaId = id(card.associacao_ambiente?.linha_id);
			const destino = linhasPorId.get(linhaId);
			const cardId = id(card.id);
			const cardAdaptado = cardParaAmbiente(entrada);
			const data = id(cardAdaptado.horario.data);
			const setorLinha = referenciaId(destino?.linha.setor);
			const setoresCard = [referenciaId(setor), referenciaId(card.setor), id(card.edicao?.contexto_competencia?.setor_id)].filter(Boolean);
			let codigo = '';
			if (!linhaId) codigo = 'CARD_SEM_ASSOCIACAO';
			else if (!destino) codigo = 'LINHA_DO_CARD_AUSENTE';
			else if (!cardId) codigo = 'CARD_SEM_IDENTIDADE';
			else if (!diasCalendario.has(data)) codigo = 'CARD_FORA_DO_CALENDARIO';
			else if (setorLinha && setoresCard.some((setorId) => setorId !== setorLinha)) codigo = 'SETOR_DO_CARD_DIVERGENTE';
			else if (idsProcessados.has(cardId) && idsProcessados.get(cardId) !== linhaId) codigo = 'ASSOCIACAO_DO_CARD_DIVERGENTE';
			if (codigo) {
				cardsOrfaos.push(card);
				inconsistencias.push({ codigo, cardId: card.id, linhaId });
				return;
			}
			if (idsProcessados.has(cardId)) return;
			idsProcessados.set(cardId, linhaId);
			const cards = destino.cards.get(data) || [];
			cards.push(cardAdaptado);
			destino.cards.set(data, cards);
		});
	}

	const linhas = Array.from(linhasPorId.values()).map(({ linha, cards, historico }) => {
		const referenciaTurno = {
			hora_inicio: linha.regime_escala?.hora_inicio || linha.hora_inicio,
			hora_fim: linha.regime_escala?.hora_fim || linha.hora_fim,
			turno: linha.regime_escala?.turno || linha.turno,
			regime: linha.regime_escala?.regime || linha.regime,
		};
		const ausenciasPorData = new Map();
		const ausenciasVistas = new Set();
		lista(linha.ausencias).forEach((ausencia) => {
			lista(ausencia.cards).forEach((card) => {
				const data = id(card.horario?.data);
				const ausenciaId = card.origem?.ausencia_id ?? ausencia.id;
				const chave = `${id(ausenciaId)}|${data}`;
				// As sinalizações administrativas não substituem registros reais.
				if (!diasCalendario.has(data) || cards.has(data) || ausenciasVistas.has(chave)) return;
				ausenciasVistas.add(chave);
				const sinalizacoes = ausenciasPorData.get(data) || [];
				sinalizacoes.push({ ...card, id: null, referencia_visual_turno: referenciaTurno, origem: { ...card.origem, ausencia_id: ausenciaId }, acoes: { ...card.acoes, pode_editar: false, pode_excluir: false, pode_trocar: false }, acoes_disponiveis: [] });
				ausenciasPorData.set(data, sinalizacoes);
			});
		});
		return {
			...linha,
			celulas: calendario.map((dia) => {
				const data = id(dia.data);
				const persistidos = cards.get(data) || [];
				const cardsDia = persistidos.length ? persistidos : ausenciasPorData.get(data) || [];
				const projecao = historico.get(data);
				const tipo = cardsDia[0]?.tipo_item;
				return {
					...dia,
					// O dia já veio marcado pelo servidor. O cabeçalho fornece apenas
					// a representação D/N/24h, sem gerar datas por recorrência.
					...(!cardsDia.length && projecao ? referenciaTurno : {}),
					status: cardsDia.length ? (tipo === 'ALOCACAO' ? 'ATRIBUIDO' : tipo) : projecao?.status || 'NAO_ATRIBUIDO',
					sugerido: !cardsDia.length && projecao?.sugerido === true,
					...(!cardsDia.length && projecao ? { projecao_historica: true } : {}),
					editavel: persistidos.some((card) => card.acoes?.pode_editar === true || card.acoes?.pode_excluir === true || card.acoes?.pode_trocar === true),
					quantidade_cards: cardsDia.length,
					cards: cardsDia,
				};
			}),
		};
	});
	return {
		ambiente: limparRegimesOrfaos({ ...resumo, calendario, linhas }),
		cardsOrfaos,
		inconsistencias,
		precisaRecarregarResumo: inconsistencias.length > 0,
	};
};
